import React, { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Calendar, Clock, BookOpen, User } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useFirebase } from "@/contexts/FirebaseContext";
import { getUserPayments } from "@/utils/firestoreService";
import { useToast } from "@/hooks/use-toast";

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  courseName: string;
  courseId: string;
  enrollmentId: string;
  createdAt: string;
  description: string;
  userId: string;
  userName: string;
  userEmail: string;
  paymentMethod: string;
  paymentType: string;
  lessonType: string;
  instructorId: string;
  instructorName: string;
  instructorEmail: string;
  hours: number;
  hoursRemaining: number;
  hoursTaken: number;
}

const PaymentHistory = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useFirebase();
  const { toast } = useToast();

  useEffect(() => {
    const fetchPayments = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const userId = currentUser.uid;
        console.log("Fetching payments for user:", userId);
        
        // Get all payments for the student
        const paymentsData = await getUserPayments(userId);
        console.log("Fetched payments:", paymentsData);
        
        if (!paymentsData || paymentsData.length === 0) {
          console.log("No payments found for user");
          setPayments([]);
          return;
        }

        // Sort payments by date, most recent first
        const sortedPayments = paymentsData.sort((a, b) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        console.log("Sorted payments:", sortedPayments);
        setPayments(sortedPayments);
      } catch (error) {
        console.error("Error fetching payments:", error);
        toast({
          title: "Error",
          description: "Failed to load payment history. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [currentUser, toast]);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <Badge className="bg-green-500 text-white">Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-500 text-white">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-500 text-white">Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const totalAmount = payments.reduce((sum, payment) => sum + payment.amount, 0);

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="container mx-auto p-6">
          <h1 className="text-2xl font-bold mb-6">Payment History</h1>
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded mb-4"></div>
            <div className="h-12 bg-gray-200 rounded mb-4"></div>
            <div className="h-12 bg-gray-200 rounded mb-4"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <div className="container mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Payment History</h1>
          <div className="text-right">
            <p className="text-sm text-gray-600">Total Amount</p>
            <p className="text-xl font-bold text-drivewise-600">{formatCurrency(totalAmount)}</p>
          </div>
        </div>
        
        {payments.length === 0 ? (
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-gray-500">
                <CreditCard className="mx-auto h-12 w-12 mb-4" />
                <p>No payment records found.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <Card key={payment.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-lg">{payment.courseName}</h3>
                        <p className="text-gray-500">{payment.description}</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {format(parseISO(payment.createdAt), 'PPP')}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {format(parseISO(payment.createdAt), 'p')}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <BookOpen className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {payment.lessonType === 'practical' ? 'Practical Lesson' : 'Individual Lesson'}
                          </span>
                        </div>
                        
                        {payment.instructorName && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-600">
                              {payment.instructorName}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {payment.hours > 0 && (
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm text-gray-600">
                            Hours: {payment.hoursTaken} taken, {payment.hoursRemaining} remaining
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right space-y-2">
                      <p className="font-bold text-lg">
                        {formatCurrency(payment.amount)}
                      </p>
                      {getStatusBadge(payment.status)}
                      <p className="text-sm text-gray-500">
                        {payment.paymentMethod === 'card' ? 'Card Payment' : payment.paymentMethod}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PaymentHistory;
